/**
 * Progress — linear progress bar.
 *
 * Two modes: determinate (provide `value` 0–100) or indeterminate (omit value).
 * Indeterminate uses a sliding pseudo-element animation so it never looks frozen.
 */
import { cn } from "@/lib/cn";

export interface ProgressProps {
  /** 0..100. Omit for an indeterminate (sliding) bar. */
  value?: number;
  /** Optional label above the bar. */
  label?: string;
  /** Optional helper text under the bar (e.g. "412/420 networks synced"). */
  hint?: string;
  className?: string;
}

export function Progress({ value, label, hint, className }: ProgressProps) {
  const indeterminate = value === undefined || value === null;
  const pct = indeterminate ? 0 : Math.max(0, Math.min(100, value));

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label || !indeterminate ? (
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-fg-dim">{label}</span>
          {!indeterminate ? (
            <span className="font-mono text-fg-mute">{Math.round(pct)}%</span>
          ) : null}
        </div>
      ) : null}
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-line/40">
        <progress className="sr-only" value={indeterminate ? undefined : pct} max={100} />
        {indeterminate ? (
          <div aria-hidden="true" className="absolute inset-y-0 w-1/3 animate-[progress-slide_1.4s_ease-in-out_infinite] rounded-full bg-aurora-grad" />
        ) : (
          <div
            aria-hidden="true"
            className="h-full w-full origin-left rounded-full bg-aurora-grad transition-transform duration-300 ease-out"
            style={{ transform: `scaleX(${pct / 100})` }}
          />
        )}
      </div>
      {hint ? <span className="text-xs text-fg-mute">{hint}</span> : null}

      {/* Keyframe registered inline (avoids touching the global CSS for one
          component). The slide is symmetric so the bar looks balanced. */}
      <style>{`
        @keyframes progress-slide {
          0% { transform: translateX(-110%); }
          50% { transform: translateX(150%); }
          100% { transform: translateX(-110%); }
        }
      `}</style>
    </div>
  );
}
